import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const UnsavedChangesContext = createContext(null);

function serialize(value) {
  try {
    return JSON.stringify(value ?? null);
  } catch {
    return String(value);
  }
}

function isInternalHref(href) {
  if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
    return false;
  }
  if (href.startsWith('http://') || href.startsWith('https://')) {
    try {
      return new URL(href).origin === window.location.origin;
    } catch {
      return false;
    }
  }
  return href.startsWith('/');
}

function toPath(href) {
  try {
    const url = new URL(href, window.location.origin);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href;
  }
}

function UnsavedChangesDialog({ open, busy, onStay, onDiscard, onSave }) {
  const { t } = useTranslation();
  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onClick={busy ? undefined : onStay} />
      <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m0 3.75h.008v.008H12v-.008ZM10.34 3.94 1.82 18.06A1.5 1.5 0 0 0 3.1 20.25h17.8a1.5 1.5 0 0 0 1.28-2.19L13.66 3.94a1.5 1.5 0 0 0-2.56 0Z" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-slate-900">{t('unsavedChanges.title')}</h2>
            <p className="mt-1 text-sm leading-6 text-slate-600">{t('unsavedChanges.message')}</p>
          </div>
        </div>
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            disabled={busy}
            onClick={onStay}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {t('unsavedChanges.stay')}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onDiscard}
            className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50"
          >
            {t('unsavedChanges.discard')}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onSave}
            className="rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
          >
            {busy ? t('forms.common.saving') : t('unsavedChanges.save')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function UnsavedChangesProvider({ children }) {
  const navigate = useNavigate();
  const guardsRef = useRef(new Map());
  const [version, setVersion] = useState(0);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const bypassRef = useRef(false);

  const getActiveGuard = useCallback(() => {
    const list = [...guardsRef.current.values()];
    for (let i = list.length - 1; i >= 0; i -= 1) {
      if (list[i].isDirty) return list[i];
    }
    return null;
  }, []);

  const proceed = useCallback(
    (target) => {
      bypassRef.current = true;
      setPending(null);
      setBusy(false);
      try {
        if (typeof target === 'number') {
          navigate(target);
        } else if (typeof target === 'string') {
          navigate(target);
        } else if (target && typeof target === 'object') {
          navigate(target.to, target.options);
        }
      } finally {
        window.setTimeout(() => {
          bypassRef.current = false;
        }, 0);
      }
    },
    [navigate],
  );

  const requestNavigate = useCallback(
    (to, options) => {
      if (bypassRef.current) {
        if (typeof to === 'number') navigate(to);
        else navigate(to, options);
        return;
      }
      const guard = getActiveGuard();
      if (!guard) {
        if (typeof to === 'number') navigate(to);
        else navigate(to, options);
        return;
      }
      setPending({ to, options, guard });
    },
    [getActiveGuard, navigate],
  );

  const register = useCallback((id, guard) => {
    guardsRef.current.set(id, guard);
    setVersion((n) => n + 1);
    return () => {
      guardsRef.current.delete(id);
      setVersion((n) => n + 1);
    };
  }, []);

  const isDirty = useMemo(() => !!getActiveGuard(), [getActiveGuard, version]);

  // Tab close / refresh
  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  // Intercept in-app <a>/<Link> clicks
  useEffect(() => {
    if (!isDirty) return undefined;
    const onClick = (e) => {
      if (bypassRef.current || e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = e.target?.closest?.('a[href]');
      if (!anchor || anchor.hasAttribute('download') || anchor.getAttribute('target') === '_blank') return;
      const href = anchor.getAttribute('href');
      if (!isInternalHref(href)) return;
      const next = toPath(href);
      const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (next === current) return;
      e.preventDefault();
      e.stopPropagation();
      requestNavigate(next);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [isDirty, requestNavigate]);

  const onStay = () => {
    if (busy) return;
    setPending(null);
  };

  const onDiscard = () => {
    if (busy || !pending) return;
    if (typeof pending.to === 'number') {
      proceed(pending.to);
    } else {
      proceed({ to: pending.to, options: pending.options });
    }
  };

  const onSave = async () => {
    if (busy || !pending) return;
    setBusy(true);
    try {
      if (typeof pending.guard.onSave === 'function') {
        await pending.guard.onSave();
      }
      if (typeof pending.to === 'number') {
        proceed(pending.to);
      } else {
        proceed({ to: pending.to, options: pending.options });
      }
    } catch {
      setBusy(false);
      setPending(null);
    }
  };

  const value = useMemo(
    () => ({ register, requestNavigate, isDirty }),
    [register, requestNavigate, isDirty],
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <UnsavedChangesDialog
        open={!!pending}
        busy={busy}
        onStay={onStay}
        onDiscard={onDiscard}
        onSave={onSave}
      />
    </UnsavedChangesContext.Provider>
  );
}

export function useSafeNavigate() {
  const ctx = useContext(UnsavedChangesContext);
  const navigate = useNavigate();
  return ctx?.requestNavigate || navigate;
}

/**
 * Register unsaved-change protection for the current page.
 *
 * @param {unknown} value Snapshot of editable state (compared via JSON.stringify)
 * @param {{ onSave?: () => Promise<void>|void, enabled?: boolean }} [options]
 * @returns {{ isDirty: boolean, markSaved: (next?: unknown) => void }}
 */
export function useUnsavedChanges(value, options = {}) {
  const { onSave, enabled = true } = options;
  const id = useId();
  const ctx = useContext(UnsavedChangesContext);
  const [baseline, setBaseline] = useState(undefined);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const serialized = useMemo(() => serialize(value), [value]);

  useEffect(() => {
    if (!enabled) return;
    if (baseline === undefined && value != null) {
      setBaseline(serialize(value));
    }
  }, [enabled, value, baseline]);

  const isDirty = enabled && baseline !== undefined && serialized !== baseline;

  const markSaved = useCallback((next) => {
    setBaseline(serialize(next !== undefined ? next : value));
  }, [value]);

  useEffect(() => {
    if (!ctx || !enabled) return undefined;
    return ctx.register(id, {
      isDirty,
      onSave: async () => {
        if (typeof onSaveRef.current === 'function') {
          await onSaveRef.current();
        }
      },
    });
  }, [ctx, id, isDirty, enabled]);

  return { isDirty, markSaved };
}
